import { compact } from 'lodash';

import {
    DecodedSensorData,
    DecodedSensorDataTypeA,
    DecodedSensorDataTypeB,
    DecodedSensorDataTypeC,
    DeviceSensorConfiguration,
} from '@/api/device/types';
import { DEFAULT_SAMPLE_INTERVAL_DURATION_MS } from '@/config';

export type SensorInfo = {
    mainSensor: {
        samplesPerMessage?: number;
        sampleIntervalMinutes: number;
        sampleDurationMinutes: number;
        firmwareSha: string;
        firmwareVersion?: string | null;
    };
    chainedSensors: {
        position: number;
        name: string;
        displayName: string;
        firmwareSha: string;
        firmwareVersion?: string | null;
    }[];
    bristlemouth: boolean;
};

const isDecodedSensorDataTypeA = (decodedConfig: DecodedSensorData): decodedConfig is DecodedSensorDataTypeA => {
    return 'sample_interval' in decodedConfig && 'sample_duration' in decodedConfig;
};
const isDecodedSensorDataTypeB = (decodedConfig: DecodedSensorData): decodedConfig is DecodedSensorDataTypeB => {
    const properties = ['bar', 'dak', 'foo', 'sampleDurationMs', 'sampleIntervalMs', 'bridgePowerControllerEnabled'];
    return properties.every((property) => property in decodedConfig);
};
const isDecodedSensorDataTypeC = (decodedConfig: DecodedSensorData): decodedConfig is DecodedSensorDataTypeC => {
    const properties = [
        'alignmentInterval5Min',
        'currentReadingPeriodMs',
        'samplesPerReport',
        'subsampleEnabled',
        'sampleDurationMs',
        'sampleIntervalMs',
    ];
    return properties.every((property) => property in decodedConfig);
};
/*
    decodedConfig can be either DecodedSensorDataTypeA or DecodedSensorDataTypeB
    TypeA has sample_interval and sample_duration fields that are numbers representing 5-minute blocks. Ex. sample_interval: 3 means 15 minutes
    TypeB has sampleIntervalMs and sampleDurationMs fields that are numbers representing milliseconds. Ex. sampleIntervalMs: 900_000 means 15 minutes
 */
const convertDecodedSensorDataToMinutes = (
    decodedConfig: DecodedSensorData
): { sampleIntervalMinutes: number; sampleDurationMinutes: number } => {
    if (isDecodedSensorDataTypeA(decodedConfig)) {
        const multiplier = Math.round(DEFAULT_SAMPLE_INTERVAL_DURATION_MS / 1000 / 60);
        const sampleIntervalMinutes = Math.round(decodedConfig.sample_interval * multiplier);
        const sampleDurationMinutes = Math.round(decodedConfig.sample_duration * multiplier);
        return { sampleIntervalMinutes, sampleDurationMinutes };
    }

    if (isDecodedSensorDataTypeB(decodedConfig)) {
        const sampleIntervalMinutes = Math.round(decodedConfig.sampleIntervalMs / 1000 / 60);
        const sampleDurationMinutes = Math.round(decodedConfig.sampleDurationMs / 1000 / 60);
        return { sampleIntervalMinutes, sampleDurationMinutes };
    }

    if (isDecodedSensorDataTypeC(decodedConfig)) {
        const sampleIntervalMinutes = Math.round(decodedConfig.sampleIntervalMs / 1000 / 60);
        const sampleDurationMinutes = Math.round(decodedConfig.sampleDurationMs / 1000 / 60);
        return { sampleIntervalMinutes, sampleDurationMinutes };
    }

    // This should never happen
    console.warn('Unknown decoded sensor data type', decodedConfig);
    return { sampleIntervalMinutes: 0, sampleDurationMinutes: 0 };
};

export const composeSensorInfo = (sensorConfig: DeviceSensorConfiguration[]): SensorInfo => {
    let bristlemouth = false;
    return compact(sensorConfig).reduce(
        (acc, sensor) => {
            // Bridge (main) sensor
            if (sensor.position === 0) {
                // This can be either DecodedSensorDataTypeA or DecodedSensorDataTypeB
                const decodedConfig = sensor.configuration.decoded;
                if ('firmware_sha' in sensor) {
                    bristlemouth = true;
                }
                const { sampleIntervalMinutes, sampleDurationMinutes } =
                    convertDecodedSensorDataToMinutes(decodedConfig);
                return {
                    ...acc,
                    bristlemouth,
                    mainSensor: {
                        samplesPerMessage:
                            'samplesPerReport' in sensor.configuration.decoded
                                ? sensor.configuration.decoded.samplesPerReport
                                : 'samples_per_message' in sensor.configuration.decoded
                                  ? sensor.configuration.decoded.samples_per_message
                                  : undefined,
                        sampleIntervalMinutes,
                        sampleDurationMinutes,
                        firmwareSha: sensor.firmware_sha,
                        firmwareVersion: sensor.firmware_version,
                    },
                };
            }

            // Chained sensors
            return {
                ...acc,
                chainedSensors: [
                    ...acc.chainedSensors,
                    {
                        position: sensor.position,
                        name: sensor.sensor_type?.name ?? sensor.app_name ?? 'Unknown sensor',
                        displayName: sensorDisplayName(sensor),
                        firmwareSha: sensor.firmware_sha,
                        firmwareVersion: sensor.firmware_version,
                    },
                ],
            };
        },
        { mainSensor: {}, chainedSensors: [], bristlemouth } as unknown as SensorInfo
    );
};

export const sensorDisplayName = (sensorConfig: DeviceSensorConfiguration, appendNodeIdToUnknown?: boolean) =>
    sensorConfig.sensor_type?.display_name ||
    sensorConfig.sensor_type?.name ||
    sensorConfig.app_name ||
    'Unknown sensor' +
        (appendNodeIdToUnknown && sensorConfig.bristlemouth_node_id ? ` ${sensorConfig.bristlemouth_node_id}` : '');

export const getFirmwareVersion = (sensor: {
    firmwareVersion?: string | null;
    firmwareSha?: string;
}): string | undefined => sensor.firmwareVersion ?? sensor.firmwareSha?.replace(/^0x/, '');
